import {UserPermissionEnum} from "../../../../utils/types/user";

export type BulkAssignmentVariant = "all" | "partial" | "none";

export type BulkAssignmentState = Readonly<{
  variant: BulkAssignmentVariant;
  assignedCount: number;
  total: number;
}>;

export type PermissionsByUserId = Readonly<
  Record<string, ReadonlyArray<UserPermissionEnum>>
>;

export const PERMISSIONS_MANAGEMENT_BULK_PATH =
  "/admin/permissions/management/bulk";

export const getBulkAssignmentState = (
  userIds: ReadonlyArray<string>,
  hasAssignment: (userId: string) => boolean,
): BulkAssignmentState => {
  const total = userIds.length;

  if (total === 0) {
    return {variant: "none", assignedCount: 0, total: 0};
  }

  const assignedCount = userIds.filter(hasAssignment).length;

  if (assignedCount === 0) {
    return {variant: "none", assignedCount, total};
  }

  if (assignedCount === total) {
    return {variant: "all", assignedCount, total};
  }

  return {variant: "partial", assignedCount, total};
};

export const formatBulkStatusLabel = (
  assignedCount: number,
  total: number,
): string => {
  if (assignedCount === 0) {
    return "none";
  }

  if (assignedCount === total) {
    return `all ${total}`;
  }

  return `${assignedCount} of ${total}`;
};

export const formatBulkCategoryBadgeLabel = (
  permissionStates: ReadonlyArray<BulkAssignmentState>,
): string => {
  if (permissionStates.length === 0) {
    return "none";
  }

  const allPermissionsAllUsers = permissionStates.every(
    state => state.variant === "all",
  );

  if (allPermissionsAllUsers) {
    return `all ${permissionStates[0]?.total ?? 0}`;
  }

  const noPermissionsAnyUser = permissionStates.every(
    state => state.variant === "none",
  );

  if (noPermissionsAnyUser) {
    return "none";
  }

  return "partial";
};

export const getBulkCategoryBadgeVariant = (
  permissionStates: ReadonlyArray<BulkAssignmentState>,
): BulkAssignmentVariant => {
  const label = formatBulkCategoryBadgeLabel(permissionStates);

  if (label === "none") {
    return "none";
  }

  if (label.startsWith("all ")) {
    return "all";
  }

  return "partial";
};

export const permissionsByUserIdFromUsers = (
  users: ReadonlyArray<{id?: string; permissions?: ReadonlyArray<UserPermissionEnum>}>,
): PermissionsByUserId =>
  Object.fromEntries(
    users
      .filter((user): user is typeof user & {id: string} => Boolean(user.id))
      .map(user => [user.id, [...(user.permissions ?? [])]]),
  );

export const applyBulkPermissionToggle = (
  permissionsByUserId: PermissionsByUserId,
  userIds: ReadonlyArray<string>,
  permission: UserPermissionEnum,
  grantToAll: boolean,
): PermissionsByUserId => {
  const nextPermissionsByUserId = {...permissionsByUserId};

  userIds.forEach(userId => {
    const currentPermissions = [...(nextPermissionsByUserId[userId] ?? [])];

    if (grantToAll) {
      if (!currentPermissions.includes(permission)) {
        currentPermissions.push(permission);
      }
    } else {
      const permissionIndex = currentPermissions.indexOf(permission);

      if (permissionIndex >= 0) {
        currentPermissions.splice(permissionIndex, 1);
      }
    }

    nextPermissionsByUserId[userId] = currentPermissions;
  });

  return nextPermissionsByUserId;
};

export const applyBulkCategoryToggle = (
  permissionsByUserId: PermissionsByUserId,
  userIds: ReadonlyArray<string>,
  permissions: ReadonlyArray<UserPermissionEnum>,
  grantToAll: boolean,
): PermissionsByUserId =>
  permissions.reduce(
    (nextPermissionsByUserId, permission) =>
      applyBulkPermissionToggle(
        nextPermissionsByUserId,
        userIds,
        permission,
        grantToAll,
      ),
    permissionsByUserId,
  );

export const isBulkPermissionPending = (
  savedPermissionsByUserId: PermissionsByUserId,
  draftPermissionsByUserId: PermissionsByUserId,
  userIds: ReadonlyArray<string>,
  permission: UserPermissionEnum,
): boolean =>
  userIds.some(userId => {
    const saved = savedPermissionsByUserId[userId] ?? [];
    const draft = draftPermissionsByUserId[userId] ?? [];

    return saved.includes(permission) !== draft.includes(permission);
  });

export const isBulkCategoryPending = (
  savedPermissionsByUserId: PermissionsByUserId,
  draftPermissionsByUserId: PermissionsByUserId,
  userIds: ReadonlyArray<string>,
  permissions: ReadonlyArray<UserPermissionEnum>,
): boolean =>
  permissions.some(permission =>
    isBulkPermissionPending(
      savedPermissionsByUserId,
      draftPermissionsByUserId,
      userIds,
      permission,
    ),
  );

export const countBulkPermissionChanges = (
  savedPermissionsByUserId: PermissionsByUserId,
  draftPermissionsByUserId: PermissionsByUserId,
  userIds: ReadonlyArray<string>,
): number =>
  userIds.reduce((total, userId) => {
    const saved = savedPermissionsByUserId[userId] ?? [];
    const draft = draftPermissionsByUserId[userId] ?? [];
    const savedSet = new Set(saved);
    const draftSet = new Set(draft);
    let changeCount = 0;

    draftSet.forEach(permission => {
      if (!savedSet.has(permission)) {
        changeCount += 1;
      }
    });

    savedSet.forEach(permission => {
      if (!draftSet.has(permission)) {
        changeCount += 1;
      }
    });

    return total + changeCount;
  }, 0);

/** Counts distinct permissions (checkbox toggles), not per-user deltas. */
export const countBulkPendingPermissionChanges = (
  savedPermissionsByUserId: PermissionsByUserId,
  draftPermissionsByUserId: PermissionsByUserId,
  userIds: ReadonlyArray<string>,
): number => {
  const permissions = new Set<UserPermissionEnum>();

  userIds.forEach(userId => {
    (savedPermissionsByUserId[userId] ?? []).forEach(permission =>
      permissions.add(permission),
    );
    (draftPermissionsByUserId[userId] ?? []).forEach(permission =>
      permissions.add(permission),
    );
  });

  let changeCount = 0;

  permissions.forEach(permission => {
    const hasChangedForAnyUser = userIds.some(userId => {
      const saved = savedPermissionsByUserId[userId] ?? [];
      const draft = draftPermissionsByUserId[userId] ?? [];

      return saved.includes(permission) !== draft.includes(permission);
    });

    if (hasChangedForAnyUser) {
      changeCount += 1;
    }
  });

  return changeCount;
};

export const formatBulkPendingChangesLabel = (
  changeCount: number,
  userCount: number,
): string =>
  `${changeCount} pending change${changeCount === 1 ? "" : "s"} across ${userCount} user${userCount === 1 ? "" : "s"}`;

export const formatBulkUserNames = (
  users: ReadonlyArray<{firstName?: string; lastName?: string}>,
  maxVisible = 2,
): string => {
  if (users.length === 0) {
    return "";
  }

  const visibleNames = users.slice(0, maxVisible).map(user => {
    const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();

    return name || "Unknown user";
  });

  const remainingCount = users.length - visibleNames.length;

  if (remainingCount <= 0) {
    return visibleNames.join(", ");
  }

  return `${visibleNames.join(", ")} + ${remainingCount} more`;
};
